import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  useAIDisabled, setAIDisabled,
  useDevMode, setDevMode,
  useColorMode, setColorMode,
  useCustomColor, setCustomColor,
  useDateFormat, setDateFormat,
  useTimeFormat, setTimeFormat,
  useTimezone, setTimezone,
  useWorkingHours, setWorkingHours,
  useOutdatedThreshold, setOutdatedThreshold,
  useSidebarOrder, setSidebarOrder as setSidebarOrderStorage,
  useAdvancedSearchViewMode, setAdvancedSearchViewMode as setAdvancedSearchViewModeStorage
} from "@/utils/settings";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { toast } from "sonner";

// Define the type for our settings
type UserSettings = {
  aiDisabled: boolean;
  devMode: boolean;
  colorMode: "light" | "dark" | "auto" | "custom";
  customColor: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  workingHoursStart: number;
  workingHoursEnd: number;
  outdatedThreshold: number; // Years threshold for outdated experience warnings
  ravePartyMode?: boolean; // Intense confetti celebration mode
  calendarSettings?: CalendarSettings;
  sidebarOrder?: string[] | null; // User's custom sidebar navigation order (array of paths)
  advancedSearchViewMode?: string; // Preferred view mode for advanced search results (card/list)
  gridSizes?: Record<string, number>; // Per-entity minimum card width in px for grid views
  daysFilters?: Record<string, number | null>; // Per-entity "last X days" filter values

  sync_init_google?: string; // ISO date string for Google calendar sync start date
  sync_init_microsoft?: string; // ISO date string for Microsoft calendar sync start date
};

// Define the type for our calendar settings
export type CalendarSettings = {
  workDayStart: number;
  workDayEnd: number;
  workWeekDays: string[];
  firstDayOfWeek: number;
  defaultEventDuration: number;
  defaultEventColor: string;
};

// Define the type for our settings context
type SettingsContextType = {
  aiDisabled: boolean;
  toggleAI: (enabled: boolean) => void;
  devMode: boolean;
  toggleDevMode: (enabled: boolean) => void;
  colorMode: "light" | "dark" | "auto" | "custom";
  setColorModeValue: (mode: "light" | "dark" | "auto" | "custom") => void;
  customColor: string;
  setCustomColorValue: (color: string) => void;
  resetToDefaultColor: () => void;
  dateFormat: string;
  setDateFormatValue: (format: string) => void;
  timeFormat: string;
  setTimeFormatValue: (format: string) => void;
  timezone: string;
  setTimezoneValue: (timezone: string) => void;
  outdatedThreshold: number;
  setOutdatedThresholdValue: (value: number) => void;
  ravePartyMode: boolean;
  toggleRavePartyMode: (enabled: boolean) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  calendarSettings: CalendarSettings;
  updateCalendarSettings: (settings: CalendarSettings) => Promise<void>;
  sidebarOrder: string[] | null;
  setSidebarOrderValue: (order: string[] | null) => void;
  advancedSearchViewMode: string;
  setAdvancedSearchViewModeValue: (mode: string) => void;
  getGridSizeForEntity: (entity: string, fallback?: number) => number;
  setGridSizeForEntity: (entity: string, size: number) => void;
  getDaysFilterForEntity: (entity: string) => number | null;
  setDaysFilterForEntity: (entity: string, days: number | null) => void;
};

// Create the context with a default value
const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Create a provider component
export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  // Initialize state from localStorage (via utility functions)
  const [aiDisabled, setAiDisabledState] = useState<boolean>(useAIDisabled());
  const [devMode, setDevModeState] = useState<boolean>(useDevMode());
  const [colorMode, setColorModeState] = useState<"light" | "dark" | "auto" | "custom">(useColorMode() as "light" | "dark" | "auto" | "custom");
  const [customColor, setCustomColorState] = useState<string>(useCustomColor());
  const [dateFormat, setDateFormatState] = useState<string>(useDateFormat());
  const [timeFormat, setTimeFormatState] = useState<string>(useTimeFormat());
  const [timezone, setTimezoneState] = useState<string>(useTimezone());
  const [outdatedThreshold, setOutdatedThresholdState] = useState<number>(useOutdatedThreshold());
  const [ravePartyMode, setRavePartyModeState] = useState<boolean>(false);
  const { startHour: workingHoursStart, endHour: workingHoursEnd } = useWorkingHours();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>({
    workDayStart: 9,
    workDayEnd: 17,
    workWeekDays: ['1', '2', '3', '4', '5'],
    firstDayOfWeek: 1,
    defaultEventDuration: 60,
    defaultEventColor: "#9b87f5"
  });
  const [sidebarOrder, setSidebarOrderState] = useState<string[] | null>(useSidebarOrder());
  const [advancedSearchViewMode, setAdvancedSearchViewModeState] = useState<string>(useAdvancedSearchViewMode());
  const [gridSizes, setGridSizesState] = useState<Record<string, number>>({});
  const [daysFilters, setDaysFiltersState] = useState<Record<string, number | null>>({});
  const sidebarSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const viewModeSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const gridSizeSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const daysFilterSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const DEFAULT_COLOR = "#0E2356";

  // Effect to load settings from Supabase when user is available
  useEffect(() => {
    if (user) {
      loadUserSettings();
    }
  }, [user]);

  // Load user settings from Supabase
  const loadUserSettings = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error("Error loading user settings:", error);
        return;
      }
      
      // Check if data.settings exists before trying to use it
      if (data && data.settings) {
        try {
          const settings = data.settings as UserSettings;
          
          // Update state and localStorage with the loaded settings
          setAiDisabledState(settings.aiDisabled);
          setAIDisabled(settings.aiDisabled);
          
          setDevModeState(settings.devMode);
          setDevMode(settings.devMode);
          
          setColorModeState(settings.colorMode);
          setColorMode(settings.colorMode);
          
          setCustomColorState(settings.customColor);
          setCustomColor(settings.customColor);
          
          setDateFormatState(settings.dateFormat);
          setDateFormat(settings.dateFormat);
          
          setTimeFormatState(settings.timeFormat);
          setTimeFormat(settings.timeFormat);

          setTimezoneState(settings.timezone || 'UTC');
          setTimezone(settings.timezone || 'UTC');

          // Set outdated threshold if it exists
          if (settings.outdatedThreshold !== undefined) {
            setOutdatedThresholdState(settings.outdatedThreshold);
            setOutdatedThreshold(settings.outdatedThreshold);
          }

          // Set rave party mode if it exists
          if (settings.ravePartyMode !== undefined) {
            setRavePartyModeState(settings.ravePartyMode);
          }

          // Set working hours if they exist
          if (settings.workingHoursStart !== undefined && settings.workingHoursEnd !== undefined) {
            setWorkingHours(settings.workingHoursStart, settings.workingHoursEnd);
          }
          
          // Set calendar settings if they exist
          if (settings.calendarSettings) {
            setCalendarSettings(settings.calendarSettings);
          }

          // Set sidebar order if it exists
          if (settings.sidebarOrder !== undefined) {
            setSidebarOrderState(settings.sidebarOrder);
            setSidebarOrderStorage(settings.sidebarOrder);
          }

          // Set advanced search view mode if it exists
          if (settings.advancedSearchViewMode) {
            setAdvancedSearchViewModeState(settings.advancedSearchViewMode);
            setAdvancedSearchViewModeStorage(settings.advancedSearchViewMode);
          }

          // Set grid sizes if they exist
          if (settings.gridSizes && typeof settings.gridSizes === 'object') {
            setGridSizesState(settings.gridSizes);
          }

          // Set days filters if they exist
          if (settings.daysFilters && typeof settings.daysFilters === 'object') {
            setDaysFiltersState(settings.daysFilters);
          }
          
          // Apply theme changes with the loaded settings
          applyThemeChanges();
          
          //console.log("Settings loaded from profile:", settings);
        } catch (e) {
          console.error("Error parsing settings:", e);
        }
      }
      
      // Reset unsaved changes after loading
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to load user settings:", err);
    }
  };

  // Save settings to Supabase
  const saveSettings = async () => {
    if (!user) {
      toast.error("You must be logged in to save settings");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Get current user settings to preserve sync dates if they exist
      const { data: userProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error("Error fetching current settings:", fetchError);
      }
      
      // Prepare settings object
      const settings: UserSettings = {
        aiDisabled,
        devMode,
        colorMode,
        customColor,
        dateFormat,
        timeFormat,
        timezone,
        workingHoursStart,
        workingHoursEnd,
        outdatedThreshold,
        ravePartyMode,
        calendarSettings,
        sidebarOrder,
        advancedSearchViewMode,
        gridSizes,
        daysFilters,
      };

      // Preserve existing sync date settings if they exist
      if (userProfile?.settings && typeof userProfile.settings === 'object') {
        const userSettings = userProfile.settings as UserSettings;
        if (userSettings.sync_init_google) {
          settings.sync_init_google = userSettings.sync_init_google;
        }
        if (userSettings.sync_init_microsoft) {
          settings.sync_init_microsoft = userSettings.sync_init_microsoft;
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          settings: settings
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      toast.success("Settings saved successfully");
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Effect to apply theme changes when they update
  useEffect(() => {
    applyThemeChanges();
    setHasUnsavedChanges(true);
  }, [colorMode, customColor]);

  // Listen for OS theme changes when in auto mode
  useEffect(() => {
    if (colorMode !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyThemeChanges();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [colorMode]);

  // Apply theme changes based on current settings
  const applyThemeChanges = () => {
    // Resolve effective dark mode
    const isDark = colorMode === 'dark'
      || (colorMode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    if (isDark)
      document.documentElement.classList.add('dark');
    else
      document.documentElement.classList.remove('dark');

    // Apply custom color if in custom mode
    if (colorMode === 'custom' && customColor) {
      document.documentElement.style.setProperty('--sidebar-background', customColor);
      document.documentElement.style.setProperty('--digger-blue', customColor);
    } else {
      // Reset to default color when not in custom mode
      document.documentElement.style.setProperty('--sidebar-background', DEFAULT_COLOR);
      document.documentElement.style.setProperty('--digger-blue', DEFAULT_COLOR);
    }
  };

  // Toggle AI processing
  const toggleAI = (enabled: boolean) => {
    const isDisabled = !enabled;
    setAiDisabledState(isDisabled);
    setAIDisabled(isDisabled);
    setHasUnsavedChanges(true);
  };

  // Toggle Dev Mode
  const toggleDevMode = (enabled: boolean) => {
    setDevModeState(enabled);
    setDevMode(enabled);
    setHasUnsavedChanges(true);
  };

  // Toggle Rave Party Mode
  const toggleRavePartyMode = (enabled: boolean) => {
    setRavePartyModeState(enabled);
    setHasUnsavedChanges(true);
  };

  // Set color mode
  const setColorModeValue = (mode: "light" | "dark" | "auto" | "custom") => {
    setColorModeState(mode);
    setColorMode(mode);
    setHasUnsavedChanges(true);
  };

  // Set custom color
  const setCustomColorValue = (color: string) => {
    setCustomColorState(color);
    setCustomColor(color);
    setHasUnsavedChanges(true);
  };

  // Reset to default color
  const resetToDefaultColor = () => {
    setCustomColorValue(DEFAULT_COLOR);
  };
  
  // Set date format
  const setDateFormatValue = (format: string) => {
    setDateFormatState(format);
    setDateFormat(format);
    setHasUnsavedChanges(true);
  };
  
  // Set time format
  const setTimeFormatValue = (format: string) => {
    setTimeFormatState(format);
    setTimeFormat(format);
    setHasUnsavedChanges(true);
  };

  // Set timezone
  const setTimezoneValue = (tz: string) => {
    setTimezoneState(tz);
    setTimezone(tz);
    setHasUnsavedChanges(true);
  };

  // Set outdated threshold
  const setOutdatedThresholdValue = (value: number) => {
    setOutdatedThresholdState(value);
    setOutdatedThreshold(value);
    setHasUnsavedChanges(true);
  };

  // Set sidebar order (persists to localStorage immediately, debounces Supabase save)
  const setSidebarOrderValue = (order: string[] | null) => {
    setSidebarOrderState(order);
    setSidebarOrderStorage(order);
    // Debounce Supabase save
    if (sidebarSaveTimerRef.current)
      clearTimeout(sidebarSaveTimerRef.current);
    sidebarSaveTimerRef.current = setTimeout(() => {
      if (!user) return;
      // Read current settings from Supabase and merge
      supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          const existing = (data?.settings as UserSettings) || {} as UserSettings;
          supabase
            .from('profiles')
            .update({ settings: { ...existing, sidebarOrder: order } })
            .eq('id', user.id)
            .then(() => {});
        });
    }, 1500);
  };

  // Set advanced search view mode (persists to localStorage immediately, debounces Supabase save)
  const setAdvancedSearchViewModeValue = (mode: string) => {
    setAdvancedSearchViewModeState(mode);
    setAdvancedSearchViewModeStorage(mode);
    if (viewModeSaveTimerRef.current)
      clearTimeout(viewModeSaveTimerRef.current);
    viewModeSaveTimerRef.current = setTimeout(() => {
      if (!user) return;
      supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          const existing = (data?.settings as UserSettings) || {} as UserSettings;
          supabase
            .from('profiles')
            .update({ settings: { ...existing, advancedSearchViewMode: mode } })
            .eq('id', user.id)
            .then(() => {});
        });
    }, 1500);
  };

  // Get grid size for a given entity (with fallback)
  const getGridSizeForEntity = (entity: string, fallback: number = 280): number => {
    const value = gridSizes[entity];
    return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
  };

  // Set grid size for a given entity — updates state immediately, debounces Supabase save
  const setGridSizeForEntity = (entity: string, size: number) => {
    const next = { ...gridSizes, [entity]: size };
    setGridSizesState(next);
    if (gridSizeSaveTimerRef.current)
      clearTimeout(gridSizeSaveTimerRef.current);
    gridSizeSaveTimerRef.current = setTimeout(() => {
      if (!user) return;
      supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          const existing = (data?.settings as UserSettings) || {} as UserSettings;
          const existingGridSizes = (existing.gridSizes && typeof existing.gridSizes === 'object') ? existing.gridSizes : {};
          supabase
            .from('profiles')
            .update({ settings: { ...existing, gridSizes: { ...existingGridSizes, [entity]: size } } })
            .eq('id', user.id)
            .then(() => {});
        });
    }, 1000);
  };

  // Get days filter for a given entity (with fallback to 60)
  const getDaysFilterForEntity = (entity: string): number | null => {
    const value = daysFilters[entity];
    return value !== undefined ? value : 60;
  };

  // Set days filter for a given entity — updates state immediately, debounces Supabase save
  const setDaysFilterForEntity = (entity: string, days: number | null) => {
    const next = { ...daysFilters, [entity]: days };
    setDaysFiltersState(next);
    if (daysFilterSaveTimerRef.current)
      clearTimeout(daysFilterSaveTimerRef.current);
    daysFilterSaveTimerRef.current = setTimeout(() => {
      if (!user) return;
      supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          const existing = (data?.settings as UserSettings) || {} as UserSettings;
          const existingDaysFilters = (existing.daysFilters && typeof existing.daysFilters === 'object') ? existing.daysFilters : {};
          supabase
            .from('profiles')
            .update({ settings: { ...existing, daysFilters: { ...existingDaysFilters, [entity]: days } } })
            .eq('id', user.id)
            .then(() => {});
        });
    }, 1000);
  };

  // Update calendar settings
  const updateCalendarSettings = async (settings: CalendarSettings) => {
    setCalendarSettings(settings);
    
    if (!user) {
      toast.error("You must be logged in to save settings");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Get current user settings to preserve sync dates if they exist
      const { data: userProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error("Error fetching current settings:", fetchError);
      }
      
      // Prepare settings object
      const updatedSettings: UserSettings = {
        aiDisabled,
        devMode,
        colorMode,
        customColor,
        dateFormat,
        timeFormat,
        timezone,
        workingHoursStart,
        workingHoursEnd,
        outdatedThreshold,
        ravePartyMode,
        calendarSettings: settings, // Use the new calendar settings
        sidebarOrder,
        gridSizes,
        daysFilters,
      };
      
      // Preserve existing sync date settings if they exist
      if (userProfile?.settings && typeof userProfile.settings === 'object') {
        const userSettings = userProfile.settings as UserSettings;
        if (userSettings.sync_init_google) {
          updatedSettings.sync_init_google = userSettings.sync_init_google;
        }
        if (userSettings.sync_init_microsoft) {
          updatedSettings.sync_init_microsoft = userSettings.sync_init_microsoft;
        }
      }
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          settings: updatedSettings 
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving calendar settings:", err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Create the context value object
  const contextValue: SettingsContextType = {
    aiDisabled,
    toggleAI,
    devMode,
    toggleDevMode,
    colorMode,
    setColorModeValue,
    customColor,
    setCustomColorValue,
    resetToDefaultColor,
    dateFormat,
    setDateFormatValue,
    timeFormat,
    setTimeFormatValue,
    timezone,
    setTimezoneValue,
    outdatedThreshold,
    setOutdatedThresholdValue,
    ravePartyMode,
    toggleRavePartyMode,
    saveSettings,
    isSaving,
    hasUnsavedChanges,
    setHasUnsavedChanges, // Expose this to allow components to trigger hasUnsavedChanges
    calendarSettings,
    updateCalendarSettings,
    sidebarOrder,
    setSidebarOrderValue,
    advancedSearchViewMode,
    setAdvancedSearchViewModeValue,
    getGridSizeForEntity,
    setGridSizeForEntity,
    getDaysFilterForEntity,
    setDaysFilterForEntity
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook for using the settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
